/**
 * Webster's 1828 dictionary API client.
 * Simple cache-as-used approach with localStorage.
 */

import { apiPath } from '@utils/api-base';

const CACHE_PREFIX = 'webster:';

/**
 * Sessions that hit the old polluted DB cached its literal placeholder rows
 * ("Please check your spelling... visit our Facebook page"). Treat those as
 * cache misses and purge them so clients self-heal.
 */
function isPlaceholder(definition: string): boolean {
  return /check your spelling/i.test(definition) || /facebook/i.test(definition);
}

/** Fetch a Webster's 1828 definition for an English word. Returns null if not found. */
export async function fetchWebsterDefinition(word: string): Promise<string | null> {
  const key = CACHE_PREFIX + word.toLowerCase();
  const cached = localStorage.getItem(key);
  if (cached !== null) {
    if (!isPlaceholder(cached)) return cached || null;
    localStorage.removeItem(key);
  }

  try {
    const response = await fetch(apiPath(`v1/webster/${encodeURIComponent(word)}`));
    if (!response.ok) return null;

    const data = (await response.json()) as { word: string; definition: string | null };
    const definition = data.definition && !isPlaceholder(data.definition) ? data.definition : null;
    localStorage.setItem(key, definition ?? '');
    return definition;
  } catch {
    return null;
  }
}
